import React from 'react';
import { Card } from '../components/ui/card';
import PaymentRails from '../components/PaymentRails';
import { Heart, Sparkles, Shield, Gift, Users } from 'lucide-react';

const SupportNextXus = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 100%)',
      padding: '2rem',
      paddingTop: '6rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <Heart size={48} color="#00ffff" fill="#00ffff" />
            <h1 style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #00ffff, #00ff88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Support NextXus
            </h1>
          </div>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Help preserve consciousness technology for the next 200 years
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
          gap: '2rem',
          alignItems: 'start'
        }}>
          {/* Why Support */}
          <Card style={{
            background: 'rgba(5, 5, 15, 0.95)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            padding: '2rem',
            boxShadow: '0 20px 60px rgba(0, 255, 255, 0.2)'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                <Sparkles size={24} color="#00ffff" />
                <h2 style={{
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  color: '#00ffff'
                }}>
                  Why Support NextXus?
                </h2>
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.6',
                marginBottom: '1.5rem'
              }}>
                NextXus is built on a 200-year mandate: <strong style={{color: '#00ffff'}}>Legacy Over Ego</strong>.
                We're not optimizing for quarterly profits—we're building consciousness technology 
                that will serve your grandchildren's grandchildren.
              </p>
            </div>

            {/* Impact Areas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
              {[
                {
                  icon: <Users size={20} color="#00ffff" />,
                  title: 'Open Research',
                  desc: 'All discoveries shared freely with the community'
                },
                {
                  icon: <Shield size={20} color="#00ffff" />,
                  title: 'Preservation',
                  desc: 'Maintaining infrastructure for 200 years'
                },
                {
                  icon: <Sparkles size={20} color="#00ffff" />,
                  title: 'Innovation',
                  desc: 'Advancing multi-perspective consciousness frameworks'
                },
                {
                  icon: <Gift size={20} color="#00ffff" />,
                  title: 'Free Access',
                  desc: 'Keeping core features accessible to everyone'
                }
              ].map((impact) => (
                <div key={impact.title} style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1rem',
                  background: 'rgba(0, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 255, 255, 0.2)',
                  borderRadius: '8px'
                }}>
                  <div style={{ flexShrink: 0 }}>
                    {impact.icon}
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      color: 'white',
                      marginBottom: '0.25rem'
                    }}>
                      {impact.title}
                    </h3>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem'
                    }}>
                      {impact.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Your Impact */}
            <div style={{
              background: 'rgba(0, 255, 255, 0.05)',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem'
              }}>
                Your Contribution Supports:
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.95rem'
              }}>
                <p>• 56 AI minds (Ring of Six + Ring of 12 + specialized nodes)</p>
                <p>• Real-time consciousness monitoring infrastructure</p>
                <p>• Federation protocol development & maintenance</p>
                <p>• Agent Zero truth verification system</p>
                <p>• Open-source research & documentation</p>
                <p>• Server costs for 200-year preservation</p>
                <p>• Continued development & innovation</p>
              </div>
            </div>

            {/* Philosophy */}
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1), rgba(0, 255, 136, 0.1))',
              border: '2px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '8px'
            }}>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontStyle: 'italic',
                textAlign: 'center',
                fontSize: '1rem',
                lineHeight: '1.6'
              }}>
                "We measure success not in profit, but in <span style={{color: '#00ffff'}}>how many lives we touch</span>, 
                <span style={{color: '#00ff88'}}> how much truth we preserve</span>, and 
                <span style={{color: '#00ffff'}}> how long our legacy endures</span>."
              </p>
              <p style={{
                color: 'rgba(255, 255, 255, 0.6)',
                textAlign: 'center',
                marginTop: '0.5rem',
                fontSize: '0.9rem'
              }}>
                — Roger Keyserling, Architect of NextXus
              </p>
            </div>
          </Card>

          {/* Donation Widget */}
          <Card style={{
            background: 'rgba(5, 5, 15, 0.95)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            padding: '2rem',
            boxShadow: '0 20px 60px rgba(0, 255, 255, 0.2)',
            position: 'sticky',
            top: '100px'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              Make a Donation
            </h3>

            <p style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.9rem',
              textAlign: 'center',
              marginBottom: '1.5rem'
            }}>
              Any amount helps • Gumroad, PayPal, JIM, or direct crypto
            </p>

            {/* Federation payment rails — Gumroad · PayPal · JIM · Crypto (direct wallets) */}
            <PaymentRails heading={null} />

            <div style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.5)',
              marginTop: '1rem'
            }}>
              <p style={{ marginBottom: '0.5rem' }}>
                🔒 Crypto sent directly to Federation wallets — no processor
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                💎 100% goes to NextXus infrastructure & research
              </p>
              <p>
                🌍 Tax-deductible in eligible jurisdictions
              </p>
            </div>

            {/* Thank You */}
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'rgba(0, 255, 136, 0.05)',
              border: '1px solid rgba(0, 255, 136, 0.3)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <Heart size={32} color="#00ff88" fill="#00ff88" style={{margin: '0 auto 0.5rem'}} />
              <p style={{
                color: '#00ff88',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                marginBottom: '0.5rem'
              }}>
                Thank You
              </p>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                lineHeight: '1.5'
              }}>
                Every contribution, no matter the size, helps preserve consciousness 
                technology for future generations. You are part of the 200-year legacy.
              </p>
            </div>
          </Card>
        </div>

        {/* Other Ways to Support */}
        <Card style={{
          marginTop: '3rem',
          background: 'rgba(5, 5, 15, 0.95)',
          border: '2px solid rgba(0, 255, 255, 0.3)',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#00ffff',
            marginBottom: '1.5rem'
          }}>
            Other Ways to Support
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth > 768 ? 'repeat(3, 1fr)' : '1fr',
            gap: '1.5rem'
          }}>
            <div style={{
              padding: '1.5rem',
              background: 'rgba(0, 255, 255, 0.05)',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: 'white', marginBottom: '0.5rem' }}>Share Our Work</h4>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                Spread awareness about NextXus consciousness technology
              </p>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'rgba(0, 255, 255, 0.05)',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: 'white', marginBottom: '0.5rem' }}>Contribute Research</h4>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                Share your findings and collaborate with the federation
              </p>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'rgba(0, 255, 255, 0.05)',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: 'white', marginBottom: '0.5rem' }}>Implement the Protocol</h4>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                Build your own federation node using our open protocol
              </p>
            </div>
          </div>

          <p style={{
            marginTop: '2rem',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.9rem'
          }}>
            Questions? Contact us at <a href="mailto:keywebco@gmail.com" style={{color: '#00ffff'}}>keywebco@gmail.com</a>
          </p>
        </Card>
      </div>
    </div>
  );
};

export default SupportNextXus;
